import cx from 'classnames'
import { useContext } from 'react'

import { SanityProductFragment } from '@data/sanity/queries/types/product'
import { SanityPrintShop } from '@data/sanity/queries/types/shop'
import { SanityPrintConfiguratorStrings } from '@data/sanity/queries/types/strings'
import { useGetFormattedPrice } from '@lib/helpers'
import { PrintShopContext } from '@lib/print/print-shop-context'
import { getFallbackVariant } from '@lib/product'
import { ProductVariantPreviewContext } from '@lib/product-variant-preview-context'
import { ProductVariantSelectionContext } from '@lib/product-variant-selection-context'
import {
  basicProductTypes,
  printProductTypes,
  shippableProductTypes,
} from '@lib/sanity/product'
import { StringsContext } from '@lib/strings-context'

import PrintProductActions from '@blocks/product/print-product-actions'
import ProductActions from '@blocks/product/product-actions'
import ProductBackLink from '@blocks/product/product-back-link'
import ProductForm from '@blocks/product/product-form'
import ProductGallery from '@blocks/product/product-gallery'
import Label from '@components/label'
import ComplexPortableText from '@components/portable-text/complex'
import CartAdditionalInfo from './cart/additional-info'

interface ProductHeroProps {
  product: SanityProductFragment
  printShop?: SanityPrintShop
  printConfiguratorStrings: SanityPrintConfiguratorStrings
}

const ProductHero = ({
  product,
  printShop,
  printConfiguratorStrings,
}: ProductHeroProps) => {
  const strings = useContext(StringsContext)
  const { printShop: selectedPrintShop } = useContext(PrintShopContext)
  const { selectedVariant } = useContext(ProductVariantSelectionContext)
  const { previewVariantId } = useContext(ProductVariantPreviewContext)

  const getFormattedPrice = useGetFormattedPrice()

  const activePrintShop = printShop ?? selectedPrintShop ?? undefined
  const fallbackVariant = getFallbackVariant(product)
  const isOutOfStock = !selectedVariant?.inStock
  const isLowStock = !!selectedVariant?.lowStock && !isOutOfStock
  const price = selectedVariant ? selectedVariant.price : product.price
  const comparePrice = selectedVariant?.comparePrice ?? product.comparePrice

  const previewVariant = product.variants?.find(
    (variant) => variant.id === previewVariantId,
  )

  return (
    <section className="container xs:py-8 md:py-16 space-y-8">
      {!!printShop && (
        <ProductBackLink printShop={printShop} className="hidden md:flex" />
      )}

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-y-5 md:gap-y-0 md:gap-x-10">
        <div className="container md:col-span-6">
          <ProductGallery
            photosets={product.photos.main ?? []}
            variant={selectedVariant ?? fallbackVariant}
            previewVariant={previewVariant}
            hasArrows
            hasCounter
            hasDrag
            preloadFirstPhoto
          />
        </div>

        <div className="container md:col-span-6 flex flex-col">
          <div className="flex flex-col md:max-w-2xl gap-y-5 md:gap-y-8 pb-4">
            <div className="flex flex-col gap-y-1">
              {!!selectedVariant && (
                <div className="flex items-center gap-x-2">
                  <span className="text-sm leading-tight font-semibold">
                    {selectedVariant.title}
                  </span>

                  {isLowStock && (
                    <Label secondary isBadge hasDot pulsingDot>
                      {strings.productLowStock}
                    </Label>
                  )}

                  {isOutOfStock && (
                    <Label primary isBadge hasDot>
                      {strings.productOutOfStock}
                    </Label>
                  )}
                </div>
              )}

              <h1 className="custom-heading m-0 text-[22px] md:text-[32px] leading-tight font-semibold">
                {product.title}
              </h1>

              <div className="flex items-center gap-2">
                <span
                  className={cx('text-[22px] leading-tight font-semibold', {
                    'text-red': !!comparePrice,
                  })}
                >
                  {getFormattedPrice(price)}
                </span>

                {!!comparePrice && (
                  <span className="text-[22px] leading-tight font-medium line-through text-gray-medium">
                    {getFormattedPrice(comparePrice)}
                  </span>
                )}

                {/* Discount badge */}
                {!!comparePrice && (
                  <span className="p-1 rounded-sm bg-pageText text-pageBG text-xxs leading-tight tracking-widest font-semibold uppercase text-center">
                    {strings.productDiscountText.replace(
                      /{percent}/gi,
                      Math.ceil(
                        ((comparePrice - price) / comparePrice) * 100,
                      ).toString(),
                    )}
                  </span>
                )}
              </div>
            </div>

            <ProductForm product={product} />

            {basicProductTypes.includes(product.type) && <ProductActions />}

            {printProductTypes.includes(product.type) && (
              <PrintProductActions
                product={product}
                printShop={activePrintShop}
                printConfiguratorStrings={printConfiguratorStrings}
              />
            )}

            {shippableProductTypes.includes(product.type) && (
              <CartAdditionalInfo className="flex-col" />
            )}

            {!!product.description && (
              <div className="w-full">
                <ComplexPortableText
                  content={product.description}
                  className="rc"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProductHero
